/**
 * Add Job From URL Modal
 * Requirements: 5.2
 * Note: Backend Lambda job-create-url (Task 8.2) not implemented
 */
import { useState, useEffect } from 'react';
import { X, Link, Loader2, AlertTriangle } from 'lucide-react';
import { api } from '../services/api';
import { useEscapeClose } from '../hooks/useEscapeClose';

interface AddJobFromURLProps {
  resumeid: string;
  onClose: () => void;
  onSuccess: () => void;
}

// Sites that block server-side scraping
const BLOCKED_DOMAINS = ['indeed.com', 'glassdoor.com', 'ziprecruiter.com'];

function isBlockedDomain(url: string): string | null {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    for (const domain of BLOCKED_DOMAINS) {
      if (hostname.includes(domain)) return domain;
    }
  } catch { /* invalid URL */ }
  return null;
}

export function AddJobFromURL({ resumeid, onClose, onSuccess }: AddJobFromURLProps) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockedWarning, setBlockedWarning] = useState<string | null>(null);

  useEscapeClose(onClose);

  // Show error if no resume is selected
  useEffect(() => {
    if (!resumeid) {
      setError('Please select a resume first from the header dropdown before adding a job.');
    }
  }, [resumeid]);

  // Check for blocked domains as user types
  useEffect(() => {
    if (url.trim()) {
      const blocked = isBlockedDomain(url);
      if (blocked) {
        setBlockedWarning(
          `${blocked.replace('.com', '').charAt(0).toUpperCase() + blocked.replace('.com', '').slice(1)} blocks automated scraping. Please use "Add Job Manually" and paste the job description instead.`
        );
      } else {
        setBlockedWarning(null);
      }
    } else {
      setBlockedWarning(null);
    }
  }, [url]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }
    if (!resumeid) {
      setError('Please select a resume first from the header dropdown before adding a job.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.createJobFromUrl(url, resumeid);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      // Extract error message from axios error response
      let errorMessage = 'Failed to create job from URL';
      if (err && typeof err === 'object') {
        const axiosError = err as { response?: { data?: { message?: string } }; message?: string };
        if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      }
      setError(errorMessage);
      console.error('Create job error:', err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Add Job from URL</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center">
              <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
              {error}
            </div>
          )}

          {blockedWarning && !error && (
            <div className="mb-4 p-3 bg-amber-50 text-amber-700 rounded-lg text-sm flex items-center">
              <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
              {blockedWarning}
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
              Job Posting URL
            </label>
            <div className="relative">
              <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="url"
                id="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/job/..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Paste the URL of the job posting. We'll extract the job details automatically.
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !url.trim() || !resumeid || !!blockedWarning}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Link className="w-4 h-4 mr-2" />
              )}
              Import Job
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
